// Ensure global indexedDB exists to prevent ReferenceErrors in environments like Safari Lockdown Mode
if (typeof window !== 'undefined' && !('indexedDB' in window)) {
  (window as any).indexedDB = null;
}

import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import * as UserRepository from './Repository/UserRepository';
import * as ContractRepository from './Repository/ContractRepository';
import * as AuthService from "./Service/AuthService";
import { Modal } from 'antd';
import { featureFlags } from "./Service/FeatureFlags";
import { BrowserRouter } from "react-router-dom";
import { Workbox } from "workbox-window";
import { User } from 'tree-wallet-client';
import ConfigurationService from "./Service/ConfigurationService";
import { updateIfAvailable } from './Service/AppUpdateService';
import { isNativeApp, purgeCachedGeoJson } from './Dependency/NativeAppMemory';
import App from "./App";
import mapboxgl from "mapbox-gl";

import '../assets/css/mapbox-gl-2.11.css';
import '../assets/less/main.less';

// mapbox-gl sends two telemetry beacons trackers as soon as an access token is set: 
// `https://events.mapbox.com/events/v2` and `https://api.mapbox.com/map-sessions/v1`.
// Both are gated on `config.EVENTS_URL`, which is a *getter* derived from `config.API_URL` -
// a plain assignment to it is silently ignored, so the property is redefined here instead.
// `API_URL` is untouched, so styles, tiles, sprites, fonts and terrain keep working as before.
Object.defineProperty(mapboxgl.config, 'EVENTS_URL', { value: null, configurable: true, writable: true });

// Desktop page zoom: a trackpad pinch arrives as a `wheel` event carrying `ctrlKey` (Chrome,
// Firefox) or as the `gesture*` events (Safari), and the browser zooms the whole page in response -
// leaving the user zoomed into a page that cannot be panned back. Those defaults are cancelled here,
// which is what the viewport meta in `index-template.html` already does on mobile.
// Gestures that start on the map are left alone: Mapbox turns them into a map zoom of its own. Same
// for the full screen photo viewer, which zooms with its own code. Only the browser's default action
// is cancelled, so both keep working; and the browser's own zoom (the `Ctrl`/`Cmd` shortcuts) is
// deliberately left available.
const isInsideMap = (target: EventTarget | null) =>
  target instanceof Element && target.closest('.mapboxgl-map') !== null;

const preventPageZoom = (event: Event) => {
  if (!isInsideMap(event.target)) {
    event.preventDefault();
  }
};

// `passive: false` is required, `preventDefault()` is ignored on a passive wheel listener.
window.addEventListener('wheel', (event: WheelEvent) => {
  // A trackpad pinch reports itself as a wheel with `ctrlKey`, like `Ctrl` + wheel with a mouse.
  if (event.ctrlKey) preventPageZoom(event);
}, { passive: false });

// Safari's trackpad pinch events. Captured on the window so they are caught wherever they are
// dispatched from and whether or not they bubble.
['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
  window.addEventListener(type, preventPageZoom, { passive: false, capture: true });
});

let modal: ReturnType<typeof Modal.info> | null = null;

function onError(message: any, level: string) {
  try {
    const maxSize = 150;
    const errorsJson = window.localStorage.getItem('errors');
    const errors = JSON.parse(errorsJson ? errorsJson : '[]');

    let messageStr = '';
    if (message instanceof Error) {
      messageStr = message.stack || message.message || String(message);
    } else if (typeof message === 'object' && message !== null) {
      try {
        messageStr = JSON.stringify(message);
      } catch (e) {
        messageStr = String(message);
      }
    } else {
      messageStr = String(message);
    }

    // Limit individual log size to 1000 characters to prevent using up localStorage quota
    if (messageStr.length > 1000) {
      messageStr = messageStr.substring(0, 1000) + '... [TRUNCATED]';
    }

    errors.push({
      level: level,
      message: messageStr,
      timestamp: new Date().getTime()
    });

    try {
      window.localStorage.setItem('errors', JSON.stringify(errors.slice(-maxSize)));
    } catch (quotaError) {
      // Storage is full — clear the error log and try again with only the latest entry
      try {
        window.localStorage.removeItem('errors');
        window.localStorage.setItem('errors', JSON.stringify([{
          level: level,
          message: messageStr,
          timestamp: new Date().getTime()
        }]));
      } catch (e) {
        // Silently suppress if still failing — never crash the app over logging
      }
    }
  } catch (err) {
    // Safe fallback: Suppress all storage exceptions (like QuotaExceededError)
    // to prevent application crashes during console.log calls.
  }
}

try {
  // The service worker is a *build* thing: test, preprod and production register it, a local dev server does not. 
  // Locally it would serve the app from whatever `public/service-worker.js` a previous build left on disk,
  // precache included, instead of the dev server's own output. webpack's `mode` is what tells them apart -
  // NODE_ENV is `production` for every build and `development` for `npm start` / `watch-dev`.
  // Don't be misled by .gitlab-ci.yml: its `NODE_ENV=development` / `NODE_ENV=prod` shell variables steer
  // build tooling only. The bundled value comes from webpack's `optimization.nodeEnv`, which defaults to
  // `mode` - and both deploy configs set `mode: 'production'` - so this is true on every deployed environment.
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const wb = new Workbox('/service-worker.js');

    // Persist the currently running app version once the SW is activated
    // This helps differentiate first installs from subsequent updates
    wb.addEventListener('activated', () => {
      try {
        ConfigurationService.setCurrentVersion();
      } catch (e) {
        console.error('Failed to set current version after SW activation', e);
      }
    });

    // Only a page that was already served by a worker can be out of date; on a first visit `controller`
    // is null and there is nothing to update from. This replaces the `version` comparison against
    // build.json, whose version is empty, plus the `installed` event that could flip it the other way
    const wasControlled = !!navigator.serviceWorker.controller;

    // Event-based only, and nothing blocks: the worker announces itself once its precache is
    // downloaded and in control, and the prompt offers the reload. The old "Update found / Please
    // wait" modal had a permanently loading OK and no way out, so a missed activation left it stuck
    let prompted = false;
    const promptForUpdate = () => {
      if (prompted || !wasControlled || featureFlags.displayUpdateModal !== true) {
        return;
      }

      prompted = true;
      if (modal) {
        modal.destroy();
      }

      modal = Modal.success({
        title: 'Update ready',
        content: (
          <div>
            <p>The new version has finished downloading.</p>
            <p>Refresh to use it.</p>
          </div>
        ),
        centered: true,
        // Above every overlay: the full-page loaders (9999) and the photo viewer (10001)
        zIndex: 10002,
        okText: 'Refresh',
        cancelText: 'Later',
        okButtonProps: { loading: false },
        onOk: () => window.location.reload(),
        onCancel: () => { prompted = false; },
      });
    };

    // Two independent signals, whichever comes first: our own "activating" message, and the moment the
    // new worker takes control of this page
    const broadcast = new BroadcastChannel('sw-update-channel');
    broadcast.onmessage = (event) => {
      console.log('[SW] received messages from broadcast channel', event);
      if (event.data && event.data.type === 'CRITICAL_SW_UPDATE_ACTIVATING') {
        promptForUpdate();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', () => promptForUpdate());

    // A worker can also be sitting ready from a previous visit (nothing above would tell us)
    navigator.serviceWorker.getRegistration()
      .then((registration) => {
        if (registration?.waiting) {
          promptForUpdate();
        }
      })
      .catch(() => undefined);

    // A check at every app start, as well as on every menu click: an app left open for hours - or
    // relaunched without touching the menu - would otherwise never notice a deploy
    wb.register()
      .then((registration) => registration?.update())
      .catch(() => undefined);

    // The app has no reload button, so a build deployed while it sits open is only noticed when
    // the user comes back to it. This is the moment a browser would have refreshed on
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void updateIfAvailable();
      }
    };
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkWhenVisible);
  }

  // In the app the shell keeps the map's own data files in SQLite (see `nativeAppMemory`)
  if (isNativeApp()) {
    void purgeCachedGeoJson();
  }

  // The trees of the queue are kept in the browser's storage, which can be reclaimed when the device runs
  // short of space - and, on iOS, deleted after 7 days without a visit when the app is not installed.
  // Asking for "persistent" storage takes the origin out of that pool where the browser supports it:
  // Chromium grants it silently to a site that is installed or that the user engages with, Firefox asks
  // the user, and Safari has no `persist()` at all. A refusal is the normal case, so the answer is only
  // reported: the debug panel shows `navigator.storage.persisted()`.
  if (navigator.storage?.persist) {
    navigator.storage.persist()
      .then((granted) => console.debug('TreeWallet: persistent storage', granted ? 'granted' : 'not granted'))
      .catch((error) => console.debug('TreeWallet: could not request persistent storage', error));
  }

  const originalConsoleError = window.console.error;
  const originalConsoleLog = window.console.log;
  const originalConsoleInfo = window.console.info;
  window.console.error = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'error');
    originalConsoleError(...args);
  };

  window.console.log = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'log');
    originalConsoleLog(...args);
  };

  window.console.info = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'info');
    originalConsoleInfo(...args);
  };

  window.document.addEventListener("error", (event) => {
    console.error('caught error', event.message);
    onError(event.message, 'error');
  });

  // Nothing else installs a rejection handler, so an `await` that failed inside a handler used to leave no
  // trace at all: this is where a site that loses its error is found
  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;

    console.error('unhandled rejection', { name: reason?.name, message: reason?.message ?? String(reason) });
  });

  window.document.addEventListener("DOMContentLoaded", async () => {
    let user: any;
    let contracts: any[] | undefined;

    try {
      [user, contracts] = await Promise.all([
        // No token, no user to ask about: `/api/user/me` can only answer 403 "Missing token", 
        // which is what anonymous visitors - and the reload that follows a logout - used to log as an error
        (AuthService.isAuthenticated() ? UserRepository.getUser() : Promise.resolve())
          .then(async () => {
            try {
              if (AuthService.isAuthenticated() && !ConfigurationService.isCurrentVersionInstalled()) {
                await UserRepository.getUser({
                  successCallback: async (user: User) => {
                    if (user) {
                      AuthService.setUser(user);
                    }
                  },
                  errorCallback: (error: Error) => {
                    console.error('there was an error fetching user data', error)
                  },
                });
              }
            } catch (err) {
              console.error('there was an unknown error', err)
            }
          }),
        ContractRepository.getContractsList()
      ]);
    } catch (error) {
      // A read that failed must not keep the app from starting: the pages say what they are missing
      console.error('could not read the user or the contracts the app starts on', { error });
    }

    /*
    if (process.env.NODE_ENV === 'production') {
      const _paq = window._paq = window._paq || [];
      // tracker methods like "setCustomDimension" should be called before "trackPageView"
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
      (function () {
        const u = "//analytics.terencepires.com/";
        _paq.push(['setTrackerUrl', u + 'matomo.php']);
        _paq.push(['setSiteId', '1']);
        if (AuthService.getUser()) {
          const user = AuthService.getUser();
          _paq.push(['setUserId', user.email]);
        }
        const d = document;
        const g = d.createElement('script')
        const s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = u + 'matomo.js';
        s.parentNode?.insertBefore(g, s);
      })();
    }
    */

    const element = document.getElementById('root');
    if (element) {
      const root = ReactDOMClient.createRoot(element);
      root.render(
        <BrowserRouter>
          {/*<React.StrictMode>*/}
          <App user={user} contracts={contracts} />
          {/*</React.StrictMode>*/}
        </BrowserRouter>,
      );
    }
  });
} catch (err) {
  console.error('there was an error', err)
}
